import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import {
  connectChatHub,
  disconnectChatHub,
  offMessageDelta,
  offMessageUpdated,
  offReconnected,
  onMessageDelta,
  onMessageUpdated,
  onReconnected,
} from '../../services/chatHub';
import type {
  ChatMessageDeltaDto,
  ChatMessageDto,
  ChatSessionDto,
  ChatSkillDto,
} from '../../types/api';
import ChatMessageItem, { ChatAttachmentView, ChatMessage } from './ChatMessage';
import ChatSessionList from './ChatSessionList';
import ChatModelSelector from './ChatModelSelector';
import ChatSkillSelector from './ChatSkillSelector';
import { CHAT_MODEL_DEFAULT, extractHtmlFromText, isHtmlReport } from '../../utils/chatHtmlReport';

interface ChatPanelProps {
  open: boolean;
  onClose: () => void;
  /** Fired when any session has an in-flight assistant reply (for launcher badge). */
  onPendingActivityChange?: (hasPending: boolean) => void;
}

interface PendingAttachment {
  id: string;
  file: File;
  previewUrl?: string;
}

const ALLOWED_ACCEPT = 'image/*,.pdf,.csv,.txt,.xlsx,.docx,.pptx';
const MAX_FILES = 10;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
/** Composer grows with content; cap so the message list keeps usable space. */
const COMPOSER_MAX_HEIGHT_PX = 200;
/** Newest messages loaded on session open / reconnect merge. */
const MESSAGES_PAGE_SIZE = 30;
/** Stick auto-scroll to bottom when within this many px of the end. */
const NEAR_BOTTOM_PX = 120;

const ALLOWED_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

const EXT_TO_MIME: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

function resolveMime(file: File): string {
  if (file.type && ALLOWED_MIME.has(file.type)) return file.type;
  const dot = file.name.lastIndexOf('.');
  if (dot >= 0) {
    const ext = file.name.substring(dot).toLowerCase();
    if (EXT_TO_MIME[ext]) return EXT_TO_MIME[ext];
  }
  return file.type || 'application/octet-stream';
}

function isAllowed(file: File): boolean {
  return ALLOWED_MIME.has(resolveMime(file));
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function dtoToMessage(m: ChatMessageDto): ChatMessage {
  const role: 'user' | 'ai' = m.role === 'user' ? 'user' : 'ai';
  const time = formatTime(m.createdAt);
  const attachments: ChatAttachmentView[] = (m.attachments ?? []).map((a) => ({
    attachmentId: a.attachmentId,
    fileName: a.fileName,
    mediaType: a.mediaType,
    sizeBytes: a.sizeBytes,
    kind: a.kind,
    webUrl: a.webUrl,
    contentUrl: a.contentUrl,
  }));

  if (role === 'ai' && m.kind === 'table' && m.columns && m.rows) {
    return {
      id: m.messageId,
      role: 'ai',
      kind: 'table',
      text: m.text,
      title: m.title ?? '',
      columns: m.columns,
      rows: m.rows,
      total: m.total,
      time,
      attachments,
      status: m.status,
      error: m.error,
    };
  }
  if (role === 'ai' && m.kind === 'canvas') {
    return {
      id: m.messageId,
      role: 'ai',
      kind: 'canvas',
      text: m.text,
      title: m.title ?? '',
      svgMarkup: m.canvasSvg,
      time,
      attachments,
      status: m.status,
      error: m.error,
    };
  }
  if (role === 'ai' && m.kind === 'html') {
    return {
      id: m.messageId,
      role: 'ai',
      kind: 'html',
      html: m.text,
      time,
      attachments,
      status: m.status,
      error: m.error,
    };
  }
  if (role === 'ai' && m.status === 'completed') {
    const extracted = extractHtmlFromText(m.text);
    if (extracted) {
      return {
        id: m.messageId,
        role: 'ai',
        kind: 'html',
        html: extracted,
        time,
        attachments,
        status: m.status,
        error: m.error,
      };
    }
  }
  return {
    id: m.messageId,
    role,
    kind: 'text',
    text: m.text,
    time,
    attachments,
    status: m.status,
    error: m.error,
  };
}

/** Merge a newest-page refetch into already-loaded older history without wiping it. */
function mergeLatestPage(prev: ChatMessage[], latest: ChatMessage[]): ChatMessage[] {
  if (latest.length === 0) return prev;
  const latestIds = new Set(latest.map((m) => m.id));
  const older = prev.filter((m) => !latestIds.has(m.id));
  return [...older, ...latest];
}

function isPersistedMessageId(id: string): boolean {
  return !id.startsWith('optimistic-') && !id.startsWith('error-');
}

const ChatPanel: React.FC<ChatPanelProps> = ({ open, onClose, onPendingActivityChange }) => {
  // -- responsive layout --
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 768px)').matches
  );

  // -- conversation state --
  const [sessions, setSessions] = useState<ChatSessionDto[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);

  // -- input state --
  const [draft, setDraft] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // -- claude status --
  const [claudeConfigured, setClaudeConfigured] = useState<boolean | null>(null);

  // -- skills --
  const [skillCatalog, setSkillCatalog] = useState<ChatSkillDto[]>([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [pendingSkillIds, setPendingSkillIds] = useState<string[]>([]);

  // -- model --
  const [selectedModel, setSelectedModel] = useState(CHAT_MODEL_DEFAULT);
  const [pendingModel, setPendingModel] = useState(CHAT_MODEL_DEFAULT);

  // -- mobile sidebar visibility --
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // -- desktop maximize (mobile is already fullscreen) --
  const [isMaximized, setIsMaximized] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  // Tracks every blob: URL we create via URL.createObjectURL so we can revoke them on unmount.
  // We can't revoke optimistic preview URLs right after send: the persisted user message in
  // `messages` keeps using them as its <img src> (to avoid a flicker while OneDrive's contentUrl
  // is still loading), so revoking early breaks the image.
  const createdBlobUrlsRef = useRef<string[]>([]);
  // Per-message last-applied delta sequence for gap detection. When a delta arrives with a
  // sequence that isn't lastSeq+1, we trigger a full-message refetch instead of guessing.
  const lastDeltaSeqRef = useRef<Map<string, number>>(new Map());
  // Latest activeSessionId snapshot read from inside SignalR handlers without re-subscribing.
  const activeSessionIdRef = useRef<string | null>(null);
  const selectedSkillIdsRef = useRef<string[]>([]);
  const messagesRef = useRef<ChatMessage[]>([]);
  const hasMoreOlderRef = useRef(false);
  const loadingOlderRef = useRef(false);
  /** When true, length changes auto-scroll to the bottom. */
  const stickToBottomRef = useRef(true);
  /** One-shot force scroll after session switch / initial load. */
  const forceScrollToBottomRef = useRef(false);
  /** True while older history is being prepended — skips stick-to-bottom auto-scroll. */
  const prependingOlderRef = useRef(false);
  /** Scroll anchor to restore after older messages mount (applied in useLayoutEffect). */
  const pendingScrollAdjustRef = useRef<{ prevHeight: number; prevTop: number } | null>(null);
  /**
   * Blocks older-page fetches until the initial/session scroll-to-bottom has settled.
   * Otherwise the top sentinel intersects at scrollTop=0 and races the forced bottom scroll.
   */
  const suppressOlderLoadRef = useRef(false);
  const loadOlderMessagesRef = useRef<() => Promise<void>>(async () => {});

  const applyMessageDelta = useCallback((delta: ChatMessageDeltaDto) => {
    const sessionId = activeSessionIdRef.current;
    if (sessionId != null && delta.sessionId !== sessionId) return;

    const lastSeq = lastDeltaSeqRef.current.get(delta.messageId) ?? 0;
    if (delta.sequence !== lastSeq + 1) {
      lastDeltaSeqRef.current.delete(delta.messageId);
      if (sessionId) {
        void apiService
          .getChatMessages(sessionId, { limit: MESSAGES_PAGE_SIZE })
          .then((page) => {
            const latest = page.items.map(dtoToMessage);
            setMessages((prev) => mergeLatestPage(prev, latest));
          })
          .catch((e) => console.error('Refetch after delta gap failed', e));
      }
      return;
    }

    lastDeltaSeqRef.current.set(delta.messageId, delta.sequence);
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== delta.messageId) return m;
        if (m.role !== 'ai' || m.kind !== 'text') return m;
        const nextText = m.text + delta.delta;
        if (isHtmlReport(nextText)) {
          return { ...m, text: '', status: 'pending', htmlGenerating: true };
        }
        return { ...m, text: nextText, status: 'pending', htmlGenerating: false };
      })
    );
  }, []);

  const applyMessageUpdated = useCallback((dto: ChatMessageDto) => {
    lastDeltaSeqRef.current.delete(dto.messageId);
    const next = dtoToMessage(dto);
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === dto.messageId);
      if (idx < 0) return [...prev, next];
      return prev.map((m) => (m.id === dto.messageId ? next : m));
    });
    if (dto.status !== 'pending') {
      setSessions((prev) =>
        prev.map((s) =>
          s.sessionId === dto.sessionId ? { ...s, hasPendingReply: false } : s
        )
      );
    }

    // After a plan-analysis HTML report, turn skills off so follow-up Q&A stays concise.
    if (
      dto.sessionId === activeSessionIdRef.current &&
      dto.role === 'assistant' &&
      dto.kind === 'html' &&
      dto.status === 'completed' &&
      selectedSkillIdsRef.current.length > 0
    ) {
      void apiService
        .updateChatSessionSkills(dto.sessionId, [])
        .then((updated) => {
          setSelectedSkillIds(updated.selectedSkillIds);
          setSessions((prev) =>
            prev.map((s) =>
              s.sessionId === dto.sessionId
                ? { ...s, selectedSkillIds: updated.selectedSkillIds }
                : s
            )
          );
        })
        .catch((e) => console.warn('Auto-deselect skills after HTML report failed', e));
    }
  }, []);

  const applyReconnected = useCallback(() => {
    const sessionId = activeSessionIdRef.current;
    if (!sessionId) return;
    lastDeltaSeqRef.current.clear();
    void apiService
      .getChatMessages(sessionId, { limit: MESSAGES_PAGE_SIZE })
      .then((page) => {
        const latest = page.items.map(dtoToMessage);
        setMessages((prev) => mergeLatestPage(prev, latest));
      })
      .catch((e) => console.error('Refetch after reconnect failed', e));
  }, []);

  const applyMessageDeltaRef = useRef(applyMessageDelta);
  applyMessageDeltaRef.current = applyMessageDelta;
  const applyMessageUpdatedRef = useRef(applyMessageUpdated);
  applyMessageUpdatedRef.current = applyMessageUpdated;
  const applyReconnectedRef = useRef(applyReconnected);
  applyReconnectedRef.current = applyReconnected;

  // ---- responsive media query ----
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  // ---- reset maximize when panel closes ----
  useEffect(() => {
    if (!open) setIsMaximized(false);
  }, [open]);

  // ---- escape restores maximize or closes panel; body scroll lock ----
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isMaximized) {
        setIsMaximized(false);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose, isMaximized]);

  // ---- auto-scroll to bottom when pinned / forced (not when prepending older history) ----
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    hasMoreOlderRef.current = hasMoreOlder;
  }, [hasMoreOlder]);

  useEffect(() => {
    if (!open) return;
    if (prependingOlderRef.current) return;
    if (!forceScrollToBottomRef.current && !stickToBottomRef.current) return;
    const wasForced = forceScrollToBottomRef.current;
    forceScrollToBottomRef.current = false;
    requestAnimationFrame(() => {
      if (prependingOlderRef.current) return;
      messagesEndRef.current?.scrollIntoView({
        behavior: wasForced ? 'auto' : 'smooth',
        block: 'end',
      });
      if (wasForced) {
        // Second frame: layout has settled at the bottom; allow top-sentinel loads.
        requestAnimationFrame(() => {
          suppressOlderLoadRef.current = false;
        });
      }
    });
  }, [open, messages.length]);

  // Restore scroll position after older messages are prepended (before paint).
  useLayoutEffect(() => {
    const pending = pendingScrollAdjustRef.current;
    if (!pending) return;
    pendingScrollAdjustRef.current = null;
    const container = messagesScrollRef.current;
    if (container) {
      container.scrollTop = pending.prevTop + (container.scrollHeight - pending.prevHeight);
    }
    prependingOlderRef.current = false;

    // IntersectionObserver only notifies on edge changes. If the sentinel is still
    // visible after prepend, chain another page without requiring a scroll away/back.
    if (
      hasMoreOlderRef.current &&
      !loadingOlderRef.current &&
      !suppressOlderLoadRef.current &&
      container &&
      container.scrollTop <= 80
    ) {
      void loadOlderMessagesRef.current();
    }
  }, [messages]);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom <= NEAR_BOTTOM_PX;
  }, []);

  // ---- auto-grow composer (unbounded rows, height-capped) ----
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [draft, open]);

  // ---- revoke object URLs on unmount ----
  useEffect(() => {
    return () => {
      createdBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      createdBlobUrlsRef.current = [];
    };
  }, []);

  // ---- initial load when panel opens ----
  const loadSessions = useCallback(async (): Promise<ChatSessionDto[]> => {
    setLoadingSessions(true);
    try {
      const list = await apiService.listChatSessions();
      setSessions(list);
      return list;
    } catch (e) {
      console.error('Failed to load chat sessions', e);
      setError('Could not load past conversations.');
      return [];
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  const loadMessages = useCallback(async (sessionId: string) => {
    setLoadingMessages(true);
    setHasMoreOlder(false);
    setLoadingOlder(false);
    loadingOlderRef.current = false;
    prependingOlderRef.current = false;
    pendingScrollAdjustRef.current = null;
    // Prevent the top sentinel from fetching older pages until we land at the bottom.
    suppressOlderLoadRef.current = true;
    try {
      const page = await apiService.getChatMessages(sessionId, { limit: MESSAGES_PAGE_SIZE });
      setMessages(page.items.map(dtoToMessage));
      setHasMoreOlder(page.hasMore);
      stickToBottomRef.current = true;
      forceScrollToBottomRef.current = true;
    } catch (e) {
      console.error('Failed to load messages', e);
      setError('Could not load conversation history.');
      setMessages([]);
      setHasMoreOlder(false);
      suppressOlderLoadRef.current = false;
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const loadOlderMessages = useCallback(async () => {
    const sessionId = activeSessionIdRef.current;
    if (
      !sessionId ||
      !hasMoreOlderRef.current ||
      loadingOlderRef.current ||
      suppressOlderLoadRef.current
    ) {
      return;
    }

    const oldest = messagesRef.current.find((m) => isPersistedMessageId(m.id));
    if (!oldest) return;

    loadingOlderRef.current = true;
    prependingOlderRef.current = true;
    stickToBottomRef.current = false;
    setLoadingOlder(true);
    const container = messagesScrollRef.current;
    const prevHeight = container?.scrollHeight ?? 0;
    const prevTop = container?.scrollTop ?? 0;

    try {
      const page = await apiService.getChatMessages(sessionId, {
        limit: MESSAGES_PAGE_SIZE,
        before: oldest.id,
      });
      const older = page.items.map(dtoToMessage);
      const existingIds = new Set(messagesRef.current.map((m) => m.id));
      const unique = older.filter((m) => !existingIds.has(m.id));
      if (unique.length === 0) {
        // Nothing new — stop pagination to avoid a tight loop at the top.
        setHasMoreOlder(false);
        prependingOlderRef.current = false;
        return;
      }
      pendingScrollAdjustRef.current = { prevHeight, prevTop };
      setMessages((prev) => {
        const existing = new Set(prev.map((m) => m.id));
        return [...unique.filter((m) => !existing.has(m.id)), ...prev];
      });
      setHasMoreOlder(page.hasMore);
    } catch (e) {
      console.error('Failed to load older messages', e);
      prependingOlderRef.current = false;
      pendingScrollAdjustRef.current = null;
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, []);

  loadOlderMessagesRef.current = loadOlderMessages;

  // Top sentinel: load older history when the user scrolls near the top.
  useEffect(() => {
    if (!open || !hasMoreOlder) return;
    const root = messagesScrollRef.current;
    const sentinel = topSentinelRef.current;
    if (!root || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          void loadOlderMessages();
        }
      },
      { root, rootMargin: '80px 0px 0px 0px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMoreOlder, activeSessionId, loadOlderMessages]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await apiService.getChatStatus();
        if (!cancelled) setClaudeConfigured(status.claudeConfigured);
      } catch {
        if (!cancelled) setClaudeConfigured(false);
      }
      try {
        const catalog = await apiService.getChatSkills();
        if (!cancelled) {
          setSkillCatalog(catalog);
          setPendingSkillIds([]);
        }
      } catch (e) {
        console.error('Failed to load chat skills', e);
      }
      const list = await loadSessions();
      if (cancelled) return;
      if (list.length > 0) {
        const first = list[0];
        setActiveSessionId(first.sessionId);
        setSelectedSkillIds(first.selectedSkillIds ?? []);
        setSelectedModel(first.selectedModel || CHAT_MODEL_DEFAULT);
        await loadMessages(first.sessionId);
      } else {
        setActiveSessionId(null);
        setMessages([]);
        setHasMoreOlder(false);
        setSelectedSkillIds([]);
        setSelectedModel(CHAT_MODEL_DEFAULT);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loadSessions, loadMessages]);

  // Keep the ref in sync so the SignalR handlers below see the current session without needing
  // to re-subscribe every time the active session changes.
  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  useEffect(() => {
    selectedSkillIdsRef.current = selectedSkillIds;
  }, [selectedSkillIds]);

  // ---- SignalR: live streaming updates while the panel is open ----
  useEffect(() => {
    if (!open) return;

    const onDelta = (delta: ChatMessageDeltaDto) => applyMessageDeltaRef.current(delta);
    const onUpdated = (dto: ChatMessageDto) => applyMessageUpdatedRef.current(dto);
    const onReconnect = () => applyReconnectedRef.current();

    onMessageDelta(onDelta);
    onMessageUpdated(onUpdated);
    onReconnected(onReconnect);

    void connectChatHub().catch((e) => console.warn('chat hub connect failed:', e));

    return () => {
      offMessageDelta(onDelta);
      offMessageUpdated(onUpdated);
      offReconnected(onReconnect);
      void disconnectChatHub();
      lastDeltaSeqRef.current.clear();
    };
  }, [open]);

  // ---- attachment helpers ----
  const handleFilesSelected = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    setError(null);

    const additions: PendingAttachment[] = [];
    for (let i = 0; i < incoming.length; i++) {
      const file = incoming[i];
      if (!isAllowed(file)) {
        setError(`"${file.name}" has an unsupported type.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(`"${file.name}" exceeds the 10 MB limit.`);
        continue;
      }
      if (pendingAttachments.length + additions.length >= MAX_FILES) {
        setError(`Up to ${MAX_FILES} files per message.`);
        break;
      }
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      if (previewUrl) createdBlobUrlsRef.current.push(previewUrl);
      additions.push({ id: `${Date.now()}-${i}-${file.name}`, file, previewUrl });
    }
    if (additions.length > 0) {
      setPendingAttachments((prev) => [...prev, ...additions]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePending = (id: string) => {
    setPendingAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
        createdBlobUrlsRef.current = createdBlobUrlsRef.current.filter(
          (u) => u !== target.previewUrl
        );
      }
      return prev.filter((a) => a.id !== id);
    });
  };

  // ---- send ----
  const handleSend = async () => {
    if (sending || isAiGenerating) return;
    const text = draft.trim();
    if (!text && pendingAttachments.length === 0) return;

    setError(null);
    setSending(true);

    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticAttachments: ChatAttachmentView[] = pendingAttachments.map((p) => ({
      attachmentId: p.id,
      fileName: p.file.name,
      mediaType: resolveMime(p.file),
      sizeBytes: p.file.size,
      kind: p.file.type.startsWith('image/') ? 'image' : 'document',
      previewUrl: p.previewUrl,
    }));

    const optimistic: ChatMessage = {
      id: optimisticId,
      role: 'user',
      kind: 'text',
      text,
      time: formatTime(new Date().toISOString()),
      attachments: optimisticAttachments,
    };
    stickToBottomRef.current = true;
    setMessages((prev) => [...prev, optimistic]);

    const filesToSend = pendingAttachments.map((p) => p.file);

    setDraft('');
    setPendingAttachments([]);

    try {
      const skillIdsForSend = activeSessionId ? undefined : pendingSkillIds;
      const modelForSend = activeSessionId ? undefined : pendingModel;
      const response = await apiService.sendChatMessage(
        activeSessionId,
        text,
        filesToSend,
        skillIdsForSend,
        modelForSend
      );
      const userMsg = dtoToMessage(response.userMessage);
      const aiMsg = dtoToMessage(response.assistantMessage);

      // Carry the optimistic image preview URLs onto the persisted user message so the previews
      // don't blink while the server-side WebUrl is still propagating.
      if (userMsg.kind === 'text' && userMsg.attachments) {
        userMsg.attachments = userMsg.attachments.map((a, idx) => ({
          ...a,
          previewUrl: optimisticAttachments[idx]?.previewUrl,
        }));
      }

      setMessages((prev) => {
        const without = prev.filter((m) => m.id !== optimisticId);
        return [...without, userMsg, aiMsg];
      });

      if (activeSessionId !== response.sessionId) {
        setActiveSessionId(response.sessionId);
        setSelectedSkillIds(pendingSkillIds);
        setSelectedModel(pendingModel);
      }
      setSessions((prev) =>
        prev.map((s) =>
          s.sessionId === response.sessionId ? { ...s, hasPendingReply: true } : s
        )
      );
      // Refresh sidebar (new session may have appeared / titles auto-updated).
      void loadSessions();
    } catch (e: unknown) {
      console.error('Chat send failed', e);
      const fallback: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'ai',
        kind: 'text',
        text:
          (e && typeof e === 'object' && 'response' in e
            ? ((e as { response?: { data?: string } }).response?.data ?? '')
            : '') || 'Failed to send. Please try again.',
        time: formatTime(new Date().toISOString()),
      };
      setMessages((prev) => [...prev, fallback]);
      setError('Send failed.');
    } finally {
      setSending(false);
      // NOTE: don't revoke the optimistic preview URLs here. The persisted user message we just
      // added to `messages` still uses them as its <img src> (see the previewUrl carry-over above)
      // so the previews keep working until the user navigates away. They're tracked in
      // createdBlobUrlsRef and released on unmount.
    }
  };

  // ---- session handlers ----
  const handleSelectSession = useCallback(
    async (sessionId: string) => {
      if (sessionId === activeSessionId) {
        setSidebarOpen(false);
        return;
      }
      const session = sessions.find((s) => s.sessionId === sessionId);
      setActiveSessionId(sessionId);
      setSelectedSkillIds(session?.selectedSkillIds ?? []);
      setSelectedModel(session?.selectedModel || CHAT_MODEL_DEFAULT);
      setSidebarOpen(false);
      await loadMessages(sessionId);
    },
    [activeSessionId, loadMessages, sessions]
  );

  const handleNewSession = useCallback(async () => {
    setActiveSessionId(null);
    setMessages([]);
    setHasMoreOlder(false);
    setPendingSkillIds([]);
    setSelectedSkillIds([]);
    setPendingModel(selectedModel);
    setSidebarOpen(false);
  }, [selectedModel]);

  const activeSkillIds = activeSessionId ? selectedSkillIds : pendingSkillIds;
  const activeModel = activeSessionId ? selectedModel : pendingModel;

  const generatingSessionIds = useMemo(() => {
    const ids = new Set<string>();
    const activeHasLocalPending =
      activeSessionId != null &&
      messages.some((m) => m.role === 'ai' && m.status === 'pending');

    for (const s of sessions) {
      if (!s.hasPendingReply) continue;
      if (s.sessionId === activeSessionId && !activeHasLocalPending) continue;
      ids.add(s.sessionId);
    }
    if (activeHasLocalPending && activeSessionId) {
      ids.add(activeSessionId);
    }
    return ids;
  }, [sessions, activeSessionId, messages]);

  const isAiGenerating =
    activeSessionId != null && generatingSessionIds.has(activeSessionId);

  useEffect(() => {
    if (!open) return;
    const hasPending =
      sessions.some((s) => s.hasPendingReply) ||
      messages.some((m) => m.role === 'ai' && m.status === 'pending');
    onPendingActivityChange?.(hasPending);
  }, [open, sessions, messages, onPendingActivityChange]);

  const handleSkillSelectionChange = useCallback(
    async (nextIds: string[]) => {
      if (activeSessionId) {
        const previous = selectedSkillIds;
        setSelectedSkillIds(nextIds);
        try {
          const updated = await apiService.updateChatSessionSkills(activeSessionId, nextIds);
          setSelectedSkillIds(updated.selectedSkillIds);
          setSessions((prev) =>
            prev.map((s) => (s.sessionId === activeSessionId ? updated : s))
          );
        } catch (e) {
          console.error('Failed to update session skills', e);
          setSelectedSkillIds(previous);
          setError('Could not update skills for this conversation.');
        }
      } else {
        setPendingSkillIds(nextIds);
      }
    },
    [activeSessionId, selectedSkillIds]
  );

  const handleModelChange = useCallback(
    async (model: string) => {
      if (activeSessionId) {
        const previous = selectedModel;
        setSelectedModel(model);
        try {
          const updated = await apiService.updateChatSessionModel(activeSessionId, model);
          setSelectedModel(updated.selectedModel);
          setSessions((prev) =>
            prev.map((s) => (s.sessionId === activeSessionId ? updated : s))
          );
        } catch (e) {
          console.error('Failed to update session model', e);
          setSelectedModel(previous);
          setError('Could not update model for this conversation.');
        }
      } else {
        setPendingModel(model);
      }
    },
    [activeSessionId, selectedModel]
  );

  const handleRenameSession = useCallback(async (sessionId: string, title: string) => {
    try {
      const updated = await apiService.renameChatSession(sessionId, title);
      setSessions((prev) => prev.map((s) => (s.sessionId === sessionId ? updated : s)));
    } catch (e) {
      console.error('Rename failed', e);
      setError('Rename failed.');
    }
  }, []);

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      try {
        await apiService.deleteChatSession(sessionId);
        setSessions((prev) => {
          const next = prev.filter((s) => s.sessionId !== sessionId);
          if (sessionId === activeSessionId) {
            if (next.length > 0) {
              setActiveSessionId(next[0].sessionId);
              void loadMessages(next[0].sessionId);
            } else {
              setActiveSessionId(null);
              setMessages([]);
              setHasMoreOlder(false);
            }
          }
          return next;
        });
      } catch (e) {
        console.error('Delete failed', e);
        setError('Delete failed.');
      }
    },
    [activeSessionId, loadMessages]
  );

  // ---- container styling ----
  const contentMaxWidth = isMaximized ? 'max-w-5xl' : 'max-w-2xl';

  const containerClasses = useMemo(() => {
    const base =
      'fixed bg-surface text-on-surface antialiased z-50 flex shadow-2xl transition-[transform,width] duration-300 ease-out';
    if (isDesktop) {
      const widthClass = isMaximized ? 'w-full' : 'w-full max-w-3xl xl:max-w-4xl';
      const borderClass = isMaximized ? '' : 'border-l border-outline';
      return [
        base,
        'top-0 right-0 h-full',
        widthClass,
        borderClass,
        open ? 'translate-x-0' : 'translate-x-full',
      ].join(' ');
    }
    return [
      base,
      'inset-0 w-full h-full',
      open ? 'translate-y-0' : 'translate-y-full',
    ].join(' ');
  }, [isDesktop, open, isMaximized]);

  const showMockHint = claudeConfigured === false;

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={-1}
        aria-label="Close chat"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Enter' && onClose()}
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          open && !isMaximized
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="AI assistant chat"
        aria-hidden={!open}
        className={containerClasses}
      >
        {/* Sidebar (always visible on >= md, slide-over on mobile) */}
        {isDesktop ? (
          <div className="w-64 shrink-0">
            <ChatSessionList
              sessions={sessions}
              activeSessionId={activeSessionId}
              generatingSessionIds={generatingSessionIds}
              loading={loadingSessions}
              onSelect={handleSelectSession}
              onNew={handleNewSession}
              onRename={handleRenameSession}
              onDelete={handleDeleteSession}
            />
          </div>
        ) : (
          <>
            <div
              role="button"
              tabIndex={-1}
              aria-label="Close session list"
              onClick={() => setSidebarOpen(false)}
              onKeyDown={(e) => e.key === 'Enter' && setSidebarOpen(false)}
              className={`absolute inset-0 bg-black/60 z-40 transition-opacity ${
                sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
              }`}
            />
            <div
              className={`absolute top-0 left-0 h-full w-72 z-50 transition-transform duration-300 ${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
            >
              <ChatSessionList
                sessions={sessions}
                activeSessionId={activeSessionId}
                generatingSessionIds={generatingSessionIds}
                loading={loadingSessions}
                onSelect={handleSelectSession}
                onNew={handleNewSession}
                onRename={handleRenameSession}
                onDelete={handleDeleteSession}
                onClose={() => setSidebarOpen(false)}
              />
            </div>
          </>
        )}

        {/* Conversation pane */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Header */}
          <header className="shrink-0 flex items-center justify-between px-4 h-16 bg-surface/90 backdrop-blur-md border-b border-slate-800/50">
            <div className="flex items-center gap-3 min-w-0">
              {!isDesktop && (
                <button
                  type="button"
                  aria-label="Open session list"
                  onClick={() => setSidebarOpen(true)}
                  className="active:scale-95 transition-transform hover:bg-slate-800/50 p-2 rounded-md text-primary"
                >
                  <span className="material-symbols-outlined">menu</span>
                </button>
              )}
              <button
                type="button"
                aria-label="Close chat"
                onClick={onClose}
                className="active:scale-95 transition-transform hover:bg-slate-800/50 p-2 rounded-md text-on-surface-variant md:hidden"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex flex-col items-end">
                <span className="font-bold tracking-widest uppercase text-[10px] text-on-surface-variant">
                  AI Assistant
                </span>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      showMockHint ? 'bg-amber-400' : 'bg-tertiary animate-pulse'
                    }`}
                  />
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider ${
                      showMockHint ? 'text-amber-400' : 'text-tertiary'
                    }`}
                  >
                    {showMockHint ? 'Mock mode' : 'Online'}
                  </span>
                </div>
              </div>
              <div className="ml-2 h-8 w-8 rounded-full bg-surface-container-high border border-outline flex items-center justify-center text-primary">
                <span
                  className="material-symbols-outlined text-lg"
                  style={{ fontVariationSettings: "'FILL' 1" }}
                >
                  architecture
                </span>
              </div>
              {isDesktop && (
                <>
                  <button
                    type="button"
                    aria-label={isMaximized ? 'Restore chat window' : 'Maximize chat window'}
                    aria-pressed={isMaximized}
                    onClick={() => setIsMaximized((v) => !v)}
                    className="ml-1 p-2 rounded-md hover:bg-slate-800/50 text-on-surface-variant transition-colors active:scale-95"
                  >
                    <span className="material-symbols-outlined">
                      {isMaximized ? 'close_fullscreen' : 'open_in_full'}
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label="Close chat"
                    onClick={onClose}
                    className="p-2 rounded-md hover:bg-slate-800/50 text-on-surface-variant transition-colors active:scale-95"
                  >
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </>
              )}
            </div>
          </header>

          {showMockHint && (
            <div className="shrink-0 px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-[11px] text-amber-300">
              {'Claude is not configured. Replies are deterministic mock data \u2014 set Claude:ApiKey to enable AI.'}
            </div>
          )}

          {error && (
            <div className="shrink-0 px-4 py-2 bg-red-500/10 border-b border-red-500/20 text-[11px] text-red-300 flex items-center justify-between">
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                className="p-1 rounded hover:bg-red-500/20"
                aria-label="Dismiss error"
              >
                <span className="material-symbols-outlined text-base">close</span>
              </button>
            </div>
          )}

          {/* Messages */}
          <div
            ref={messagesScrollRef}
            onScroll={handleMessagesScroll}
            className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-4 py-6 bg-surface"
          >
            <div className={`${contentMaxWidth} mx-auto space-y-6`}>
              <div ref={topSentinelRef} className="h-px w-full" aria-hidden="true" />
              {loadingOlder && (
                <div className="text-center text-slate-400 text-[11px] py-1">
                  Loading earlier messages...
                </div>
              )}
              {loadingMessages && messages.length === 0 && (
                <div className="text-center text-slate-400 text-xs py-12">
                  Loading conversation...
                </div>
              )}
              {!loadingMessages && messages.length === 0 && activeSessionId == null && (
                <div className="text-center text-slate-400 text-xs py-12">
                  Start a conversation by sending a message or attaching a file below.
                </div>
              )}
              {messages.map((m) => (
                <ChatMessageItem key={m.id} message={m} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Pending attachment preview strip */}
          {pendingAttachments.length > 0 && (
            <div className="shrink-0 border-t border-outline/50 bg-surface-container-low/80 px-4 py-3">
              <div className={`${contentMaxWidth} mx-auto flex flex-wrap gap-2`}>
                {pendingAttachments.map((p) => {
                  const isImage = p.file.type.startsWith('image/');
                  return (
                    <div
                      key={p.id}
                      className="relative flex items-center gap-2 px-3 py-2 bg-surface-container-high border border-outline rounded-lg max-w-[18rem]"
                    >
                      {isImage && p.previewUrl ? (
                        <img
                          src={p.previewUrl}
                          alt={p.file.name}
                          className="w-8 h-8 rounded object-cover shrink-0"
                        />
                      ) : (
                        <span className="material-symbols-outlined text-primary text-xl shrink-0">
                          {p.file.type === 'application/pdf' ? 'picture_as_pdf' : 'attach_file'}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-200 truncate" title={p.file.name}>
                          {p.file.name}
                        </p>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest">
                          {formatBytes(p.file.size)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePending(p.id)}
                        aria-label={`Remove ${p.file.name}`}
                        className="p-1 rounded hover:bg-elevation-dark text-slate-300 hover:text-slate-100"
                      >
                        <span className="material-symbols-outlined text-base">close</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Skills + input (ARCH_OS template shell) */}
          <div className="shrink-0 px-4 pb-4 pt-2 bg-surface-container-low/80 backdrop-blur-md border-t border-slate-800/50">
            <div className={`${contentMaxWidth} mx-auto`}>
              <div className="flex flex-wrap items-center gap-3 pb-3">
                {claudeConfigured && (
                  <ChatModelSelector
                    selectedModel={activeModel}
                    onChange={(m) => void handleModelChange(m)}
                    disabled={sending}
                  />
                )}
                {claudeConfigured && skillCatalog.length > 0 && (
                  <ChatSkillSelector
                    skills={skillCatalog}
                    selectedIds={activeSkillIds}
                    onChange={(ids) => void handleSkillSelectionChange(ids)}
                    disabled={sending}
                  />
                )}
              </div>
              <div className="flex items-end gap-2 bg-surface-container-highest border border-outline p-1 rounded-xl shadow-inner">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_ACCEPT}
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
                <button
                  type="button"
                  aria-label="Attach files"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  className="p-2 text-on-surface-variant hover:text-on-surface transition-colors disabled:opacity-40 shrink-0"
                >
                  <span className="material-symbols-outlined">attach_file</span>
                </button>
                <textarea
                  ref={composerRef}
                  rows={1}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void handleSend();
                    }
                  }}
                  placeholder={
                    sending
                      ? 'Sending...'
                      : isAiGenerating
                        ? 'AI is responding...'
                        : 'Ask about blueprints or costs...'
                  }
                  disabled={sending}
                  aria-label="Message"
                  className="flex-1 bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-sm text-on-surface py-2 placeholder:text-on-surface-variant/50 disabled:opacity-60 min-w-0 resize-none overflow-y-auto leading-5"
                  style={{ maxHeight: COMPOSER_MAX_HEIGHT_PX }}
                />
                <button
                  type="button"
                  aria-label="Send message"
                  onClick={() => void handleSend()}
                  disabled={
                    sending ||
                    isAiGenerating ||
                    (!draft.trim() && pendingAttachments.length === 0)
                  }
                  className="bg-primary text-on-primary p-2 rounded-lg active:scale-90 transition-transform disabled:opacity-40 shrink-0 mb-0.5"
                >
                  {sending ? (
                    <span className="material-symbols-outlined animate-spin text-sm">sync</span>
                  ) : (
                    <span
                      className="material-symbols-outlined"
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >
                      send
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default ChatPanel;
